import { useToast } from "components/atoms/toast/toast-context";
import { FormGroupField } from "components/web-checkout/form-group/form-group";
import { useAddLocation } from "hooks/pages/use-add-location/use-add-location";
import { useUserValidation } from "hooks/pages/use-user-validation/use-user-validation";
import Image from "next/image";
import { useRouter } from "next/router";
import LeftArrowIcon from "public/assets/arrow_left.svg";
import IndonesiaFlag from "public/assets/indo-flag.svg";
import ActiveLocationIcon from "public/assets/location-details/active-location.svg";
import ActiveSearchIcon from "public/assets/location-details/active-search.svg";
import LocationIcon from "public/assets/location-details/location.svg";
import SearchIcon from "public/assets/search.svg";
import React, { useRef, useState } from "react";
import { containsNonASCII } from "utils/utils";
import { parentFormFields, userFormFields } from "./edit-userdata.data";
import {
  BackButtonContainer,
  ButtonContainer,
  Card,
  CardHeading,
  CardSubtitle,
  CountryCodeContainer,
  CountryCodeText,
  FlagIcon,
  FormContainer,
  HintText,
  IconContainer,
  InputContainer,
  InputWrapper,
  Label,
  PhoneInput,
  PhoneInputContainer,
  ProgressBarBackground,
  ProgressBarContainer,
  ProgressBarFill,
  ProgressContainer,
  ProgressText,
  StyledInput,
  SubmitButton
} from "./edit-userdata.styles";
import {
  IEditUserData,
  ParentData,
  Step,
  UserData
} from "./edit-userdata.types";

export const EditUserData: React.FC<IEditUserData> = ({
  userData,
  parentData,
  onComplete,
  isLoading = false
}) => {
  const router = useRouter();
  const { addToast } = useToast();
  const [currentStep, setCurrentStep] = useState<Step>("user-data");
  const [userFormData, setUserFormData] = useState<UserData>(userData);
  const [parentFormData, setParentFormData] = useState<ParentData>(parentData);
  const {
    userErrors,
    parentErrors,
    locationErrors,
    validateUserData,
    validateParentData,
    validateLocationData
  } = useUserValidation();

  const { cityOptions, schoolOptions, isCustomCity, isCustomSchool } =
    useAddLocation({
      city: userFormData.residence,
      school: userFormData.schoolName
    });

  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleUserInputChange = (field: keyof UserData, value: string) => {
    setUserFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleParentInputChange = (field: keyof ParentData, value: string) => {
    setParentFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUserDataSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (containsNonASCII(userFormData)) {
      addToast({
        heading:
          "Beberapa karakter yang kamu masukkan tidak didukung. Silakan coba lagi dengan teks biasa",
        variant: "error"
      });
      return;
    }
    const isUserValid = validateUserData(userFormData);
    const isLocationValid = validateLocationData({
      residence: userFormData.residence,
      schoolName: userFormData.schoolName
    });

    if (!isUserValid || !isLocationValid) return;
    setCurrentStep("parent-data");
  };

  const handleParentDataSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (containsNonASCII(parentFormData)) {
      addToast({
        heading:
          "Beberapa karakter yang kamu masukkan tidak didukung. Silakan coba lagi dengan teks biasa",
        variant: "error"
      });

      return;
    }
    if (!validateParentData(parentFormData)) return;
    onComplete(userFormData, parentFormData);
  };

  const handleBack = () => {
    if (currentStep === "user-data") {
      router.back();
    } else {
      setCurrentStep("user-data");
    }
  };

  const isValid = (userData: UserData, parentData: ParentData) => {
    return (
      userData.name &&
      userData.email &&
      userData.grade &&
      userData.curriculum &&
      userData.residence &&
      userData.schoolName &&
      parentData.parentName &&
      parentData.parentPhone
    );
  };

  const isUserDataStep = currentStep === "user-data";
  const progressPercentage = isUserDataStep ? 50 : 100;
  const stepText = isUserDataStep ? "Langkah 1/2" : "Langkah 2/2";
  const heading = isUserDataStep ? "Data murid" : "Data orang tua";

  return (
    <>
      <BackButtonContainer onClick={handleBack}>
        <Image src={LeftArrowIcon} alt="back" width={24} height={24} />
        Kembali
      </BackButtonContainer>
      <Card>
        <CardHeading>{heading}</CardHeading>
        <CardSubtitle>
          Pastiin semua data di bawah udah benar sebelum lanjut ya
        </CardSubtitle>

        <ProgressContainer>
          <ProgressBarContainer>
            <ProgressBarBackground />
            <ProgressBarFill progress={progressPercentage} />
          </ProgressBarContainer>
          <ProgressText>{stepText}</ProgressText>
        </ProgressContainer>

        <form
          onSubmit={
            isUserDataStep ? handleUserDataSubmit : handleParentDataSubmit
          }
        >
          <FormContainer>
            {isUserDataStep
              ? userFormFields.map((field) => {
                  if (field.key === "residence") {
                    return (
                      <FormGroupField
                        key={field.key}
                        label={field.label}
                        placeholder="Contoh: Jakarta"
                        value={userFormData[field.key]}
                        changeHandler={(value: string) =>
                          handleUserInputChange(field.key, value)
                        }
                        options={cityOptions}
                        isCustom={isCustomCity}
                        activeIcon={ActiveLocationIcon}
                        inactiveIcon={LocationIcon}
                        margin="10px 0 10px 0"
                        errorMessage={locationErrors[field.key]}
                      />
                    );
                  }

                  if (field.key === "schoolName") {
                    return (
                      <FormGroupField
                        key={field.key}
                        label={field.label}
                        placeholder="Contoh: SMAN 1 Jakarta"
                        value={userFormData[field.key]}
                        changeHandler={(value: string) =>
                          handleUserInputChange(field.key, value)
                        }
                        options={schoolOptions}
                        isCustom={isCustomSchool}
                        notFoundMessage="Pastikan nama sekolahmu benar, ya"
                        activeIcon={ActiveSearchIcon}
                        inactiveIcon={SearchIcon}
                        errorMessage={locationErrors[field.key]}
                      />
                    );
                  }

                  return (
                    <InputWrapper key={field.key}>
                      <Label htmlFor={field.key}>{field.label}</Label>
                      <InputContainer>
                        <StyledInput
                          ref={(el: HTMLInputElement | null) =>
                            (inputRefs.current[field.key] = el)
                          }
                          id={field.key}
                          type={field.type}
                          value={userFormData[field.key]}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleUserInputChange(field.key, e.target.value)
                          }
                          disabled={field.disabled}
                          hasIcon={field.hasIcon}
                          hasError={!!userErrors[field.key]}
                        />
                        {field.hasIcon && field.icon && (
                          <IconContainer>{field.icon}</IconContainer>
                        )}
                      </InputContainer>
                      {userErrors[field.key] ? (
                        <HintText isError={!!userErrors[field.key]}>
                          {userErrors[field.key]}
                        </HintText>
                      ) : (
                        field.hintText && <HintText>{field.hintText}</HintText>
                      )}
                    </InputWrapper>
                  );
                })
              : parentFormFields.map((field) => (
                  <InputWrapper key={field.key}>
                    <Label htmlFor={field.key}>{field.label}</Label>
                    {field.key === "parentPhone" ? (
                      <PhoneInputContainer>
                        <CountryCodeContainer>
                          <FlagIcon>
                            <Image
                              src={IndonesiaFlag}
                              alt="Indonesia Flag"
                              width={16}
                              height={16}
                            />
                          </FlagIcon>
                          <CountryCodeText>+62</CountryCodeText>
                        </CountryCodeContainer>
                        <PhoneInput
                          id={field.key}
                          type={field.type}
                          value={parentFormData[field.key]}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleParentInputChange(field.key, e.target.value)
                          }
                          hasError={!!parentErrors[field.key]}
                        />
                      </PhoneInputContainer>
                    ) : (
                      <InputContainer>
                        <StyledInput
                          id={field.key}
                          type={field.type}
                          value={parentFormData[field.key]}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleParentInputChange(field.key, e.target.value)
                          }
                          hasIcon={field.hasIcon}
                          hasError={!!parentErrors[field.key]}
                        />
                        {field.hasIcon && field.icon && (
                          <IconContainer>{field.icon}</IconContainer>
                        )}
                      </InputContainer>
                    )}
                    {parentErrors[field.key] && (
                      <HintText isError={!!parentErrors[field.key]}>
                        {parentErrors[field.key]}
                      </HintText>
                    )}
                    {field.hintText && <HintText>{field.hintText}</HintText>}
                  </InputWrapper>
                ))}
          </FormContainer>

          <ButtonContainer>
            <SubmitButton
              type="submit"
              disabled={isLoading || !isValid(userFormData, parentFormData)}
            >
              {isLoading ? "Loading..." : isUserDataStep ? "Lanjut" : "Selesai"}
            </SubmitButton>
          </ButtonContainer>
        </form>
      </Card>
    </>
  );
};
